import React, { useState, useEffect } from 'react';
import SimpleRichEditor from './SimpleRichEditor';
import MarkdownIt from 'markdown-it';

// @ts-ignore
const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  breaks: true,
  highlight: (str: string, lang: string) => {
    if (lang && lang.match(/^python|javascript|js|typescript|ts|bash|shell|html|css|json|xml|yaml|yml$/)) {
      return `<pre><code class="language-${lang}">${str}</code></pre>`;
    }
    return `<pre><code>${str}</code></pre>`;
  }
});

// Функція для перетворення простого тексту в HTML з переносами
const convertTextToHtml = (text: string): string => {
  // Спочатку пробуємо markdown
  let html = md.render(text);
  
  // Якщо текст не мав markdown синтаксису, конвертуємо прості переноси рядків
  if (html === `<p>${text}</p>\n`) {
    // Розділяємо по подвійних переносах для параграфів
    const paragraphs = text.split(/\n\n+/).filter(p => p.trim());
    if (paragraphs.length > 1) {
      html = paragraphs.map((p: string) => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('\n');
    }
  }
  
  return html;
};

interface InformationPage {
  _id: string;
  title: string;
  slug: string;
  content: string;
  description: string;
  category: string;
  isPublished: boolean;
  featured: boolean;
  tags: string[];
  imageUrl?: string;
  author: string;
  priority: number;
  status: string;
  createdAt?: string;
}

interface InformationPageFormData {
  title: string;
  slug: string;
  content: string;
  description: string;
  category: string;
  isPublished: boolean;
  featured: boolean;
  tags: string;
  imageUrl: string;
  author: string;
  priority: number;
}

const CreateInformationPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<InformationPage | null>(null);
  const [informationPages, setInformationPages] = useState<InformationPage[]>([]);

  const [formData, setFormData] = useState<InformationPageFormData>({
    title: '',
    slug: '',
    content: '',
    description: '',
    category: 'general',
    isPublished: true,
    featured: false,
    tags: '',
    imageUrl: '',
    author: 'DUSKPROOF Team',
    priority: 0
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: target.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'priority' ? parseInt(value) || 0 : value
      }));
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .trim();
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: generateSlug(title)
    }));
  };

  // Функція для завантаження Information Pages
  const fetchInformationPages = async () => {
    try {
      // Clear old localStorage data
      localStorage.removeItem('informationPages');
      
      // Load from API with httpOnly cookie credentials
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const response = await fetch(`${apiUrl}/api/information-pages/admin-all`, {
        credentials: 'include', // Include httpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        // API returns { success: true, data: [...] }
        const pages = result.data || result;
        setInformationPages(Array.isArray(pages) ? pages : []);
        console.log('Loaded information pages from API:', pages.length);
      } else {
        setInformationPages([]);
        console.log('No information pages found on server');
      }
    } catch (error) {
      console.error('Error fetching information pages:', error);
      setInformationPages([]);
    }
  };

  useEffect(() => {
    fetchInformationPages();
  }, []);

  const toggleForm = () => {
    setShowForm(!showForm);
    if (showForm) {
      setEditingItem(null);
      clearForm();
    }
  };

  const clearForm = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      description: '',
      category: 'general',
      isPublished: true,
      featured: false,
      tags: '',
      imageUrl: '',
      author: 'DUSKPROOF Team',
      priority: 0
    });
  };

  const handleEdit = (item: InformationPage) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      slug: item.slug,
      content: item.content,
      description: item.description,
      category: item.category,
      isPublished: item.isPublished,
      featured: item.featured,
      tags: item.tags.join(', '),
      imageUrl: item.imageUrl || '',
      author: item.author,
      priority: item.priority
    });
    setShowForm(true);
  };

  const handleDelete = async (itemId: string) => {
    if (window.confirm('Are you sure you want to delete this information page?')) {
      try {
        console.log('🗑️  Deleting information page:', itemId);
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        const response = await fetch(`${apiUrl}/api/information-pages/${itemId}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        console.log('📊 Delete response status:', response.status);

        if (response.ok) {
          console.log('✅ Delete successful, reloading data from server');
          // Перезавантажуємо дані з сервера
          await fetchInformationPages();
          alert('Information page deleted successfully!');
        } else {
          const errorData = await response.json();
          console.error('❌ Delete error:', errorData);
          alert('Error deleting information page: ' + (errorData.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('❌ Error deleting information page:', error);
        alert('Error deleting information page');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Валідація обов'язкових полів
    if (!formData.title.trim()) {
      alert('Please enter a page title');
      return;
    }

    if (!formData.content.trim()) {
      alert('Please enter page content');
      return;
    }

    try {
      // ✅ Конвертуємо текст/Markdown в HTML з підтримкою переносів рядків
      const htmlContent = convertTextToHtml(formData.content);
      console.log('HTML Content:', htmlContent); // DEBUG

      const pageData = {
        title: formData.title,
        slug: formData.slug.startsWith('/') ? formData.slug.substring(1) : formData.slug,
        content: htmlContent,
        description: formData.description,
        category: formData.category,
        isPublished: formData.isPublished,
        featured: formData.featured,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        imageUrl: formData.imageUrl,
        author: formData.author,
        priority: formData.priority,
        status: formData.isPublished ? 'published' : 'draft'
      };

      console.log('Creating Information Page:', pageData);
      console.log('Form data slug:', formData.slug);
      console.log('Page data slug:', pageData.slug);

      if (editingItem) {
        // Update existing
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        const response = await fetch(`${apiUrl}/api/information-pages/${editingItem._id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(pageData)
        });

      if (response.ok) {
          const result = await response.json();
          console.log('✅ Update response:', result);
          alert('Information Page updated successfully!');
          setEditingItem(null);
          setShowForm(false);
          clearForm();
          await fetchInformationPages();
        } else {
          const error = await response.text();
          console.error('❌ Update error:', error);
          alert(`Error updating: ${error}`);
        }
      } else {
        // Create new
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        console.log('📤 Sending POST to:', `${apiUrl}/api/information-pages`);
        console.log('📋 Data:', JSON.stringify(pageData, null, 2));
        
        const response = await fetch(`${apiUrl}/api/information-pages`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(pageData)
        });

        console.log('📊 Response status:', response.status);
        console.log('📊 Response ok:', response.ok);

        if (response.ok) {
          const result = await response.json();
          console.log('✅ Create response:', result);
          alert('Information Page created successfully!');
          setShowForm(false);
          clearForm();
          await fetchInformationPages();
        } else {
          const error = await response.text();
          console.error('❌ Error response:', error);
          alert(`Error creating: ${error}`);
        }
      }

    } catch (error) {
      console.error('Error creating Information Page:', error);
      alert('Error creating Information Page');
    }
  };

  // Фільтрація за пошуковим запитом
  const filteredPages = informationPages.filter(item =>
    (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.category && item.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.author && item.author.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="text-gray-100 font-sans h-full" style={{backgroundColor: '#0a0908', padding: '2rem'}}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-dusk-amber to-dusk-copper bg-clip-text text-transparent mb-2">Information Pages Management</h1>
            <p className="text-gray-400">Manage documentation and information pages</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search pages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50 focus:border-dusk-copper transition-all"
              />
              <svg
                className="absolute left-3 top-3.5 h-5 w-5 text-[var(--dusk-faint)]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            {/* Update button - show only when editing */}
            {editingItem && showForm && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(e);
                }}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Update Page
              </button>
            )}
            
            <button
              onClick={() => {
                if (!showForm) {
                  setEditingItem(null);
                  clearForm();
                }
                toggleForm();
              }}
              className="px-6 py-3 bg-gradient-to-r from-dusk-copper to-dusk-amber hover:from-dusk-copper-deep hover:to-dusk-copper text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/40"
            >
              {showForm ? (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Page
                </>
              )}
            </button>
          </div>
        </div>

        {/* Create Information Page Form */}
        {showForm && (
          <div className="p-6 mb-8 rounded-2xl border border-[var(--dusk-line)] bg-[rgba(243,235,226,0.04)] backdrop-blur-sm">
          <h2 className="text-xl font-bold text-white mb-6">
            Create New Page
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Page URL */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Page URL
              </label>
              <div className="flex items-center">
                <span className="text-gray-400 mr-2">/</span>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  required
                  className="flex-1 px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                  placeholder="terms-of-use"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">URL for the page (auto-generated from title, but can be edited)</p>
            </div>

            {/* Page Content Section */}
            <div className="border-t border-[var(--dusk-line-strong)] pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Page Content</h3>

              {/* Title */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleTitleChange}
                  required
                  className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                  placeholder="Stake the future."
                />
              </div>

              {/* Subtitle */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Subtitle</label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                  placeholder="Join DUSKPROOF — where decentralization meets automation."
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <SimpleRichEditor
                  value={formData.content}
                  onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                  placeholder="Enter article text... Use Markdown for formatting.
**bold** or *italic*
# Heading
- list
> quote
`code`
[link](url)
Text alignment buttons
Image upload with alignment buttons"
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex space-x-4 pt-6 border-t border-[var(--dusk-line-strong)]">
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-teal-700 to-cyan-700 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-teal-500/30"
              >
                {editingItem ? 'Update Information Page' : 'Create Information Page'}
              </button>
              <button
                type="button"
                onClick={toggleForm}
                className="px-6 py-3 bg-[rgba(243,235,226,0.06)]/50 hover:bg-gray-600/50 text-gray-200 font-semibold rounded-xl transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
          </div>
        )}

        {/* Information Pages Table */}
        <div className="rounded-2xl border border-[var(--dusk-line)] overflow-hidden bg-[rgba(243,235,226,0.03)] backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[rgba(243,235,226,0.05)] border-b border-[var(--dusk-line)]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Page
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Visibility
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {filteredPages.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center">
                      <div className="text-gray-400">
                        <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-lg font-medium">
                          {informationPages.length === 0 ? 'No information pages yet' : 'No pages found'}
                        </p>
                        <p className="text-sm">
                          {informationPages.length === 0 ? 'Click "Add Page" to create your first information page' : 'Try adjusting your search terms'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredPages.map((item) => (
                    <tr key={item._id} className="hover:bg-[rgba(243,235,226,0.05)] transition-all">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {item.imageUrl ? (
                              <img
                                className="h-10 w-10 rounded-lg object-cover"
                                src={item.imageUrl}
                                alt={item.title}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.parentElement!.innerHTML = `
                                    <div class="h-10 w-10 rounded-lg bg-gray-600 flex items-center justify-center">
                                      <svg class="h-6 w-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                                      </svg>
                                    </div>
                                  `;
                                }}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-lg bg-gray-600 flex items-center justify-center">
                                <svg className="h-6 w-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">
                              {item.title}
                            </div>
                            <div className="text-sm text-gray-400 max-w-xs truncate">
                              {item.description}
                            </div>
                            <div className="flex gap-2 mt-1">
                              <span className="text-xs text-dusk-amber">{item.category}</span>
                              <span className="text-xs text-green-400">Priority: {item.priority}</span>
                              {item.author && <span className="text-xs text-purple-400">by {item.author}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.status === 'active' ? 'bg-green-800 text-green-100' :
                          item.status === 'inactive' ? 'bg-red-800 text-red-100' :
                          'bg-green-800 text-green-100'
                        }`}>
                          {item.status || 'active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.isPublished ? 'bg-green-800 text-green-100' : 'bg-red-800 text-red-100'
                          }`}>
                            {item.isPublished ? 'Public' : 'Admin Only'}
                          </span>
                          {item.featured && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-800 text-yellow-100">
                              Featured
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => {
                              const slug = item.slug.startsWith('/') ? item.slug.substring(1) : item.slug;
                              const pageUrl = `${window.location.origin}/${slug}`;
                              window.open(pageUrl, '_blank');
                            }}
                            className="p-2 text-dusk-amber hover:text-dusk-amber hover:bg-[rgba(208,0,104,0.15)] rounded-lg transition-all"
                            title="View Page"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all"
                            title="Edit"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(item._id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all"
                            title="Delete"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateInformationPage;
